import React from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaModalIconWrapper from "../../ui-kit/kuika-modal-icon-wrapper";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaText from "../../ui-kit/typography/Text/Text";
import "./branching-modal.scss";

export interface IRestoreCommitModalProps {
  showModal: boolean;
  /** "restore" = whole-branch snapshot restore; "rewind" = rewind to at/before a version. */
  mode: "restore" | "rewind";
  /** Human label for the target checkpoint/commit (e.g. "commit #12" or a milestone title). */
  targetLabel?: string;
  inProgress?: boolean;
  /**
   * Where the restore stands (`KuikaButton` `status`). Given, the confirm button carries it —
   * the busy word, then its ✓ or "Try again" — and `inProgress` no longer draws on it.
   */
  status?: KuikaButtonStatus;
  onClose: () => void;
  onConfirm: () => void;
}

const RESTORE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Restoring", success: "Restored" };
const REWIND_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Rewinding", success: "Rewound" };

const RestoreCommitModal: React.FC<IRestoreCommitModalProps> = ({
  showModal,
  mode,
  targetLabel,
  inProgress,
  status,
  onClose,
  onConfirm
}) => {
  const isRestore: boolean = mode === "restore";
  const title: string = isRestore ? "Restore checkpoint" : "Rewind branch";
  const target: string = targetLabel ? `“${targetLabel}”` : "this point";
  const description: string = isRestore
    ? `The entire branch will be restored to the saved checkpoint at ${target}. Any commits and staged changes made after it will be discarded. This cannot be undone.`
    : `The branch will be rewound to ${target}. Any commits and staged changes made after that version will be discarded. This cannot be undone.`;

  const footer = (
    <KModalFooter
      closesOnSuccess
      onCancel={onClose}
      submitLabel={isRestore ? "Restore" : "Rewind"}
      submitTone="danger"
      onSubmit={onConfirm}
      loading={inProgress}
      status={status}
      statusLabels={isRestore ? RESTORE_STATUS_LABELS : REWIND_STATUS_LABELS}
    />
  );

  return (
    <KModal showModal={showModal} closeModal={onClose} header={title} footer={footer} width={360} centered>
      <div className="kuika-branching-modal">
        <div className="kuika-branching-modal__header">
          <KuikaModalIconWrapper iconName="restore" intent="destructive" />
          <div className="kuika-branching-modal__title">
            <KuikaH5>{title}</KuikaH5>
          </div>
        </div>
        <div className="kuika-branching-modal__content">
          <div className="kuika-branching-modal__description">
            <KuikaText>{description}</KuikaText>
          </div>
        </div>
      </div>
    </KModal>
  );
};

export default RestoreCommitModal;
